import React, { useState, useEffect } from "react";
import {
  MapPin, Phone, Mail, Clock, ShieldCheck, Repeat, Sparkles, Store,
  Menu, X, Quote, BadgeCheck, MessageCircle, Info, Scale, ThumbsUp, Eye, CheckCircle2, Handshake, Facebook, Users,
} from "lucide-react";
import {
  COVO, HIGHLIGHTS, PRODUCTS, CATEGORIES, FEATURED_REVIEWS, REVIEWS, ORDER_STEPS,
} from "./covoData";
import ConceptCTAPill from "@/components/ConceptCTAPill";

/*
  COVO 24K GOLD HK — fully self-contained website (concept/demo).
  Everything lives under src/covo + public/covo. It imports nothing from the
  MySiteCare site, so this folder can be exported and hosted independently.
*/

function Disclaimer() {
  return (
    <div data-testid="covo-disclaimer" className="bg-[#2A2318] text-[#E9DFC4] text-center text-xs sm:text-sm px-4 py-2.5 border-b border-[#C8A14A]/20">
      <span className="inline-flex items-center gap-2">
        <Info size={14} className="text-[#C8A14A] shrink-0" />
        Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with COVO Jewelry.
      </span>
    </div>
  );
}

function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const s = () => setScrolled(window.scrollY > 10);
    s(); window.addEventListener("scroll", s);
    return () => window.removeEventListener("scroll", s);
  }, []);
  const links = [["About", "#about"], ["Collections", "#collections"], ["Reviews", "#reviews"], ["Visit Us", "#visit"]];
  return (
    <header className={`sticky top-0 z-50 transition-all ${scrolled ? "bg-[#1A1712]/95 backdrop-blur-md shadow-lg" : "bg-[#1A1712]"}`}>
      <nav className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-[70px]">
        <a href="#top" data-testid="covo-logo" className="flex items-center gap-3">
          <img src="/covo/logo.jpg" alt="COVO Jewelry" className="h-11 w-11 rounded-full object-cover ring-2 ring-[#C8A14A]" />
          <div className="leading-tight">
            <span className="block font-bold text-[#F3E7C6] tracking-wide covo-serif" style={{ fontSize: 20 }}>COVO JEWELRY</span>
            <span className="block text-[10px] uppercase tracking-[0.25em] text-[#C8A14A]">24K Hong Kong Gold</span>
          </div>
        </a>
        <div className="hidden md:flex items-center gap-8">
          {links.map(([l, h]) => <a key={h} href={h} className="text-sm font-medium text-[#E9DFC4] hover:text-[#C8A14A] transition-colors">{l}</a>)}
          <a href="#visit" className="rounded-full bg-gradient-to-r from-[#C8A14A] to-[#9A7A2E] px-5 py-2 text-sm font-semibold text-[#1A1712] hover:brightness-110 transition">Contact</a>
        </div>
        <button className="md:hidden text-[#F3E7C6]" onClick={() => setOpen(!open)} data-testid="covo-menu-toggle" aria-label="Menu">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>
      {open && (
        <div className="md:hidden bg-[#1A1712] border-t border-[#C8A14A]/20 px-4 py-3 space-y-1">
          {links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block px-2 py-3 text-[#E9DFC4] font-medium">{l}</a>)}
          <a href="#visit" onClick={() => setOpen(false)} className="block text-center rounded-full bg-[#C8A14A] px-5 py-3 font-semibold text-[#1A1712]">Contact</a>
        </div>
      )}
    </header>
  );
}

export default function CovoSite() {
  const [productsExpanded, setProductsExpanded] = useState(false);
  const [reviewsExpanded, setReviewsExpanded] = useState(false);
  return (
    <div id="top" className="covo-root bg-[#FBF6EC] text-[#1A1712] min-h-screen pb-16 md:pb-0">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@500;600;700&family=Jost:wght@300;400;500;600&display=swap');
        .covo-root{font-family:'Jost',sans-serif;}
        .covo-root h1,.covo-root h2,.covo-root h3,.covo-serif{font-family:'Cormorant Garamond',serif;}
        .gold-text{background:linear-gradient(90deg,#C8A14A,#E8C97A,#9A7A2E);-webkit-background-clip:text;background-clip:text;color:transparent;}
        .gold-line{height:2px;width:70px;background:linear-gradient(90deg,#C8A14A,transparent);}
        .covo-disclosure{min-height:44px;border:1px solid #9A7A2E;padding:.7rem 1rem;color:#6A521C;background:#fff;font-size:.75rem;font-weight:700;}
        @media(max-width:639px){.covo-secondary:not(.is-revealed){display:none}.covo-hero-image{object-position:76% center!important}.covo-hero-overlay{background:linear-gradient(90deg,rgba(26,23,18,.96) 0%,rgba(26,23,18,.82) 62%,rgba(26,23,18,.48) 100%)!important}}
        @media(min-width:640px){.covo-disclosure{display:none}}
      `}</style>

      <Disclaimer />
      <Nav />

      {/* HERO */}
      <section className="relative">
        <img src="/covo/banner.webp" alt="COVO 24K Hong Kong Gold jewelry" className="covo-hero-image w-full h-[42vh] md:h-[60vh] object-cover" style={{ objectPosition: "74% center" }} />
        <div className="covo-hero-overlay absolute inset-0" style={{ background: "linear-gradient(90deg,rgba(26,23,18,.92) 0%,rgba(26,23,18,.66) 48%,rgba(26,23,18,.08) 82%)" }} />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 w-full">
            <div className="max-w-xl">
              <span className="inline-flex items-center gap-2 rounded-full bg-[#C8A14A]/15 border border-[#C8A14A]/40 px-4 py-1.5 text-xs font-medium text-[#F3E7C6]">
                <Sparkles size={13} className="text-[#C8A14A]" /> 24K Hong Kong Gold in Manila
              </span>
              <h1 className="mt-5 text-4xl sm:text-5xl md:text-6xl font-bold text-[#FBF6EC] leading-[1.05]">
                COVO JEWELRY
                <span className="block gold-text">24K Hong Kong Gold</span>
              </h1>
              <p className="mt-5 text-base md:text-lg text-[#E9DFC4]/90 leading-relaxed">
                Discover 24K gold jewelry, Piyao charms, bracelets, chains and custom pieces from COVO's Manila stores in Ongpin and Chinatown Gold Center.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <a href="#collections" className="rounded-full bg-gradient-to-r from-[#C8A14A] to-[#9A7A2E] px-7 py-3 text-sm font-semibold text-[#1A1712] hover:brightness-110 transition">Browse Collection</a>
                <a href="#visit" className="rounded-full border border-[#C8A14A]/60 px-7 py-3 text-sm font-semibold text-[#F3E7C6] hover:bg-[#C8A14A]/10 transition">Visit Our Stores</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP — social stats separated from purity claim */}
      <section className="bg-[#1A1712] text-[#F3E7C6]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            [COVO.fbFollowers, "Facebook followers"],
            [COVO.fbRecommend, "Recommended on Facebook"],
            [`${COVO.fbReviews}`, "Facebook reviews"],
            ["Since 2019", "Documented presence"],
          ].map(([a, b]) => (
            <div key={b}>
              <div className="text-2xl md:text-3xl font-bold gold-text covo-serif">{a}</div>
              <div className="text-xs md:text-sm text-[#C9BE9E] mt-1">{b}</div>
            </div>
          ))}
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-6">
          <p className="text-center text-xs text-[#A89A76]">
            <span className="font-semibold text-[#C8A14A]">Specialists in 24K Hong Kong gold.</span> {COVO.purityNote}
          </p>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="max-w-6xl mx-auto px-4 sm:px-6 py-12 md:py-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <img src="/covo/logo.jpg" alt="COVO Jewelry logo" className="w-36 h-36 rounded-2xl object-cover mb-6 shadow-md" />
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#9A7A2E]">About COVO</span>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold">24K Hong Kong gold, the Ongpin way</h2>
            <div className="gold-line my-5" />
            <p className="text-[#4A4234] leading-relaxed">{COVO.intro}</p>
            <p className="mt-4 text-[#4A4234] leading-relaxed">
              COVO is especially known for combining tiny 24K charms with bead bracelets, and offers trade-ins, restringing and in-store gold testing.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {HIGHLIGHTS.map((h, i) => {
              const Icon = [ShieldCheck, Repeat, Sparkles, Store][i];
              return (
                <div key={h.title} className="rounded-2xl bg-white border border-[#EADFC2] p-6 shadow-sm hover:shadow-md transition">
                  <div className="w-11 h-11 rounded-xl bg-[#C8A14A]/15 flex items-center justify-center mb-4"><Icon size={22} className="text-[#9A7A2E]" /></div>
                  <h3 className="text-lg font-semibold">{h.title}</h3>
                  <p className="mt-1.5 text-sm text-[#4A4234] leading-relaxed">{h.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* COLLECTIONS */}
      <section id="collections" className="bg-[#F4EAD4]/60 py-12 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#9A7A2E]">Collections</span>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold">Handcrafted in 24K gold</h2>
            <div className="gold-line mx-auto my-5" />
            <p className="text-[#4A4234]">Many pieces are individually tagged with purity and weight. Message COVO to confirm current availability and today's price.</p>
          </div>

          {/* COVO rate note */}
          <div className="mx-auto max-w-2xl rounded-xl bg-white border border-[#EADFC2] p-4 mb-8 flex items-start gap-3">
            <Scale size={18} className="text-[#9A7A2E] mt-0.5 shrink-0" />
            <p className="text-sm text-[#4A4234]">
              <span className="font-semibold">Today's COVO 24K rate</span> is set by COVO and confirmed per item. Final price depends on the piece's design, workmanship and weight — ask staff for the current per-gram rate.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PRODUCTS.map((p, index) => (
              <div key={p.name} data-testid={`covo-product-${p.name}`} className={`group rounded-2xl overflow-hidden bg-white border border-[#EADFC2] shadow-sm hover:shadow-lg transition flex flex-col${index >= 3 ? " covo-secondary" : ""}${productsExpanded ? " is-revealed" : ""}`}>
                <div className="aspect-square overflow-hidden bg-[#FBF6EC]">
                  <img src={p.img} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="text-xl font-semibold">{p.name}</h3>
                  <p className="mt-1 text-sm text-[#4A4234]">{p.desc}</p>
                  <div className="mt-4 space-y-1.5 text-sm">
                    <div className="flex items-center gap-2 text-[#9A7A2E] font-medium"><Sparkles size={14} /> {p.purity}</div>
                    <div className="flex items-center gap-2 text-[#4A4234]"><Scale size={14} className="text-[#9A7A2E]" /> Approx. weight: <span className="font-medium">{p.weight}</span></div>
                  </div>
                  <a href={COVO.messenger} target="_blank" rel="noopener noreferrer" data-testid="covo-inquire" className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-[#1A1712] px-5 py-2.5 text-sm font-semibold text-[#F3E7C6] hover:bg-[#2A2318] transition">
                    <MessageCircle size={15} className="text-[#C8A14A]" /> Ask about this piece
                  </a>
                </div>
              </div>
            ))}
          </div>
          {PRODUCTS.length > 3 && <div className="mt-6 text-center"><button type="button" className="covo-disclosure" aria-expanded={productsExpanded} onClick={() => setProductsExpanded((value) => !value)}>{productsExpanded ? "Show fewer pieces" : `View all ${PRODUCTS.length} pieces`}</button></div>}

          <div className="mt-10 rounded-2xl bg-[#1A1712] p-8 md:p-10">
            <h3 className="text-[#F3E7C6] text-2xl font-bold covo-serif">What we carry</h3>
            <div className="gold-line my-4" />
            <div className="flex flex-wrap gap-2.5">
              {CATEGORIES.map((c) => <span key={c} className="rounded-full border border-[#C8A14A]/40 bg-[#C8A14A]/10 px-3.5 py-1.5 text-sm text-[#E9DFC4]">{c}</span>)}
            </div>
          </div>
        </div>
      </section>

      {/* HOW ORDERING WORKS */}
      <section id="how-it-works" className="max-w-6xl mx-auto px-4 sm:px-6 py-12 md:py-20">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#9A7A2E]">How It Works</span>
          <h2 className="mt-2 text-3xl md:text-4xl font-bold">Simple, no-pressure ordering</h2>
          <div className="gold-line mx-auto my-5" />
          <p className="text-[#4A4234]">This site helps you browse and inquire — every purchase is confirmed and completed directly with COVO.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {ORDER_STEPS.map((s, i) => {
            const Icon = [Eye, MessageCircle, CheckCircle2, Handshake][i];
            return (
              <div key={s.n} data-testid={`covo-step-${s.n}`} className="rounded-2xl bg-white border border-[#EADFC2] p-6 shadow-sm text-center">
                <div className="mx-auto w-12 h-12 rounded-full bg-[#C8A14A]/15 flex items-center justify-center mb-4"><Icon size={22} className="text-[#9A7A2E]" /></div>
                <div className="text-xs font-bold text-[#C8A14A] tracking-widest">STEP {s.n}</div>
                <h3 className="mt-1 text-lg font-semibold">{s.title}</h3>
                <p className="mt-1.5 text-sm text-[#4A4234] leading-relaxed">{s.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* REVIEWS */}
      <section id="reviews" className="max-w-6xl mx-auto px-4 sm:px-6 py-12 md:py-20">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#9A7A2E]">What Customers Say</span>
          <h2 className="mt-2 text-3xl md:text-4xl font-bold">Trusted by long-term buyers</h2>
          <div className="gold-line mx-auto my-5" />
          <span className="text-[#4A4234] text-sm">{COVO.fbRecommend} recommend COVO on Facebook · {COVO.fbReviews} reviews</span>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {FEATURED_REVIEWS.map((r) => (
            <div key={r.name} className="rounded-2xl bg-white border border-[#EADFC2] p-7 shadow-sm">
              <Quote size={24} className="text-[#C8A14A]" />
              <p className="mt-3 text-[#3A3327] leading-relaxed">“{r.text}”</p>
              <div className="mt-5 flex items-center gap-2">
                <BadgeCheck size={16} className="text-[#9A7A2E]" />
                <span className="text-sm font-semibold">{r.name}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {REVIEWS.map((r, index) => (
            <div key={r.name} className={`rounded-xl bg-[#FBF6EC] border border-[#EADFC2] p-5${index >= 3 ? " covo-secondary" : ""}${reviewsExpanded ? " is-revealed" : ""}`}>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#C8A14A]/15 px-2.5 py-1 text-[11px] font-semibold text-[#9A7A2E]">
                <ThumbsUp size={12} /> Recommended
              </span>
              <p className="mt-2.5 text-sm text-[#3A3327] leading-relaxed">“{r.text}”</p>
              <div className="mt-3 text-xs font-semibold text-[#6A5E42]">— {r.name}</div>
            </div>
          ))}
        </div>
        {REVIEWS.length > 3 && <div className="mt-6 text-center"><button type="button" className="covo-disclosure" aria-expanded={reviewsExpanded} onClick={() => setReviewsExpanded((value) => !value)}>{reviewsExpanded ? "Show fewer reviews" : `Show all ${REVIEWS.length} reviews`}</button></div>}
      </section>

      {/* VISIT / CONTACT */}
      <section id="visit" className="bg-[#1A1712] py-12 md:py-20 text-[#F3E7C6]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-12">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C8A14A]">Visit Us</span>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold text-[#FBF6EC]">Find us in Ongpin, Manila</h2>
            <div className="gold-line my-5" />
            <p className="text-sm text-[#C9BE9E] mb-6">Contact details below are from public listings. Please confirm current branch hours and availability before visiting.</p>
            <ul className="space-y-5">
              <li className="flex items-start gap-4">
                <span className="rounded-xl bg-[#C8A14A]/15 p-3"><MapPin size={20} className="text-[#C8A14A]" /></span>
                <div><div className="text-xs uppercase tracking-wide text-[#C9BE9E]">Ongpin Branch</div><div className="text-[#E9DFC4]">{COVO.addressMain}</div></div>
              </li>
              <li className="flex items-start gap-4">
                <span className="rounded-xl bg-[#C8A14A]/15 p-3"><Store size={20} className="text-[#C8A14A]" /></span>
                <div><div className="text-xs uppercase tracking-wide text-[#C9BE9E]">Chinatown Gold Center — M07/M08</div><div className="text-[#E9DFC4]">{COVO.addressBranch}</div></div>
              </li>
              <li className="flex items-start gap-4">
                <span className="rounded-xl bg-[#C8A14A]/15 p-3"><Phone size={20} className="text-[#C8A14A]" /></span>
                <div><div className="text-xs uppercase tracking-wide text-[#C9BE9E]">Contact numbers (public listings)</div>
                  {COVO.phones.map((p) => <a key={p} href={`tel:${p.replace(/\s/g, "")}`} className="block text-[#E9DFC4] hover:text-[#C8A14A]">{p}</a>)}
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="rounded-xl bg-[#C8A14A]/15 p-3"><MessageCircle size={20} className="text-[#C8A14A]" /></span>
                <div><div className="text-xs uppercase tracking-wide text-[#C9BE9E]">Preferred online contact</div>
                  <a href={COVO.messenger} target="_blank" rel="noopener noreferrer" className="text-[#E9DFC4] hover:text-[#C8A14A]">Message COVO on Facebook</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="rounded-xl bg-[#C8A14A]/15 p-3"><Facebook size={20} className="text-[#C8A14A]" /></span>
                <div><div className="text-xs uppercase tracking-wide text-[#C9BE9E]">Facebook page</div>
                  <a href={COVO.facebook} target="_blank" rel="noopener noreferrer" data-testid="covo-facebook-link" className="text-[#E9DFC4] hover:text-[#C8A14A]">Visit COVO on Facebook</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="rounded-xl bg-[#C8A14A]/15 p-3"><Users size={20} className="text-[#C8A14A]" /></span>
                <div><div className="text-xs uppercase tracking-wide text-[#C9BE9E]">Community</div>
                  <a href={COVO.communityLink} target="_blank" rel="noopener noreferrer" data-testid="covo-community-link" className="text-[#E9DFC4] hover:text-[#C8A14A]">{COVO.communityName}</a>
                  <div className="text-xs text-[#C9BE9E] mt-0.5">{COVO.communityMembers}</div>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="rounded-xl bg-[#C8A14A]/15 p-3"><Mail size={20} className="text-[#C8A14A]" /></span>
                <div><div className="text-xs uppercase tracking-wide text-[#C9BE9E]">Email</div><a href={`mailto:${COVO.email}`} className="text-[#E9DFC4] hover:text-[#C8A14A] break-all">{COVO.email}</a></div>
              </li>
              <li className="flex items-start gap-4">
                <span className="rounded-xl bg-[#C8A14A]/15 p-3"><Clock size={20} className="text-[#C8A14A]" /></span>
                <div><div className="text-xs uppercase tracking-wide text-[#C9BE9E]">Hours</div><div className="text-[#E9DFC4]">{COVO.hours}</div></div>
              </li>
            </ul>
          </div>
          <div className="rounded-2xl overflow-hidden border border-[#C8A14A]/30 min-h-[340px]">
            <iframe title="COVO location map" src={COVO.mapEmbed} className="w-full h-full min-h-[340px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#12100C] text-[#C9BE9E]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src="/covo/logo.jpg" alt="COVO" className="h-12 w-12 rounded-full object-cover ring-2 ring-[#C8A14A]" />
              <div>
                <div className="font-bold text-[#F3E7C6] covo-serif text-lg">COVO JEWELRY</div>
                <div className="text-xs uppercase tracking-[0.25em] text-[#C8A14A]">24K Hong Kong Gold</div>
              </div>
            </div>
            <div className="text-sm"><a href={`mailto:${COVO.email}`} className="hover:text-[#C8A14A]">{COVO.email}</a> · {COVO.phones[0]}</div>
          </div>
          <div className="mt-8 pt-6 border-t border-[#C8A14A]/15 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
            <p>© {new Date().getFullYear()} COVO Jewelry — 24K Hong Kong Gold.</p>
            <p>Concept website by <a href="https://mysitecare.eu" target="_blank" rel="noopener noreferrer" className="text-[#C8A14A] hover:underline">MySiteCare</a></p>
          </div>
          <p className="mt-4 text-[11px] text-[#7A715A] leading-relaxed">
            This is an unsolicited concept website created by MySiteCare. It is not operated by or affiliated with COVO Jewelry. Gold purity, weight and pricing should always be confirmed and tested in person before purchase.
          </p>
        </div>
      </footer>

      {/* Sticky mobile contact */}
      <a href={COVO.messenger} target="_blank" rel="noopener noreferrer" data-testid="covo-sticky-contact"
        className="md:hidden fixed bottom-4 inset-x-4 z-50 rounded-full bg-gradient-to-r from-[#C8A14A] to-[#9A7A2E] px-6 py-3.5 text-center text-sm font-semibold text-[#1A1712] shadow-xl flex items-center justify-center gap-2">
        <MessageCircle size={17} /> Message COVO
      </a>
      <ConceptCTAPill slug="covo" name={COVO.name} />
    </div>
  );
}
